import React from "react"

/**
 * Manages scroll events for a given container, invoking specific callbacks when the top
 * or bottom scroll threshold is reached.
 *
 * @param {Function} onScrollTop A callback function to be executed when the user scrolls to the top of the container.
 * @param {Function} onScrollBottom A callback function to be executed when the user
 * scrolls to the bottom of the container.
 * @param {number} [scrollThreshold=1] An optional threshold value to determine the sensitivity of scroll detection.
 * @return {Function} Returns a scroll event handler function that can be attached
 * to the `onScroll` event of a scrollable container.
 */
export function useScroll(
  onScrollTop?: () => void,
  onScrollBottom?: () => void,
  scrollThreshold = 8,
): UseScrollReturn {
  return (event) => {
    const target = event.currentTarget as HTMLDivElement
    const { scrollTop, clientHeight, scrollHeight } = target

    const bottom = Math.ceil(scrollTop + clientHeight)
    const height = Math.floor(scrollHeight)
    const isAtTop = scrollTop <= 0
    const isAtBottom = bottom >= height - scrollThreshold

    if (isAtTop) {
      onScrollTop?.()
    }
    else if (isAtBottom) {
      onScrollBottom?.()
    }
  }
}

interface UseScrollReturn {
  (event: React.UIEvent<HTMLElement>): void,
}
